import {
  Cause,
  Effect,
  Logger,
  type LogLevel,
  ManagedRuntime,
  References,
} from 'effect';

type LogMethod = (objOrMsg?: unknown, msg?: string) => void;

/**
 * Effect-backed app logger for non-Effect codepaths (React components, event
 * handlers, zustand stores). The facade keeps the small pino call surface the
 * apps used — `info/warn/error/debug(obj, msg?)` plus `child(bindings,
 * { level? })` — but every record is an `Effect.logWithLevel` run on a
 * dedicated logging ManagedRuntime: bindings are `Effect.annotateLogs`
 * annotations, a logged `Error` (positional or under an `err` key) rides the
 * record's cause channel, and per-child levels are provided via the
 * `References.MinimumLogLevel` reference. Code already inside an Effect should
 * use `Effect.log*` directly, not this.
 *
 * Secrets: there is intentionally no key-based scrubbing here. Sensitive
 * values must be `Redacted.make` at the source (Effect loggers print
 * `<redacted>`), and `@effect/platform` `Headers` self-redact
 * authorization/cookie/set-cookie/x-api-key via `Headers.currentRedactedNames`,
 * so logged HttpClient errors are already safe.
 */
export type AppLogger = {
  debug: LogMethod;
  info: LogMethod;
  warn: LogMethod;
  error: LogMethod;
  child: (
    bindings: Record<string, unknown>,
    options?: { level?: LogLevel.LogLevel },
  ) => AppLogger;
};

/** Component prop shape for threading a parent logger + level override. */
export type LogSettings = {
  parentLogger: AppLogger;
  logOptions?: { level?: LogLevel.LogLevel };
};

export type MakeAppLoggerOptions = {
  /** Static bindings attached to every record (e.g. `{ app, env }`). */
  base?: Record<string, unknown>;
  level?: LogLevel.LogLevel;
  /** 'pretty' for dev/browser consoles, 'json' for lambda/CloudWatch. */
  format?: 'pretty' | 'json';
};

export function makeAppLogger(options: MakeAppLoggerOptions = {}): AppLogger {
  const runtime = ManagedRuntime.make(
    Logger.layer([
      options.format === 'json' ? Logger.consoleJson : Logger.consolePretty(),
    ]),
  );

  const make = (
    bindings: Record<string, unknown>,
    level: LogLevel.LogLevel,
  ): AppLogger => {
    const log =
      (methodLevel: LogLevel.Severity): LogMethod =>
      (objOrMsg, msg) => {
        let message: string;
        let annotations: Record<string, unknown> = bindings;
        let cause: Cause.Cause<unknown> = Cause.empty;

        if (objOrMsg instanceof Error) {
          message = msg ?? objOrMsg.message;
          cause = Cause.fail(objOrMsg);
        } else if (typeof objOrMsg === 'object' && objOrMsg !== null) {
          // pino parity: `msg` promotes to the record message, an Error under
          // `err` rides the cause channel; everything else is an annotation.
          const {
            msg: objMsg,
            err,
            ...rest
          } = objOrMsg as Record<string, unknown>;
          if (err instanceof Error) cause = Cause.fail(err);
          else if (err !== undefined) rest.err = err;
          message = msg ?? (typeof objMsg === 'string' ? objMsg : '');
          annotations = { ...bindings, ...rest };
        } else {
          message =
            typeof objOrMsg === 'string'
              ? objOrMsg
              : objOrMsg === null || objOrMsg === undefined
                ? ''
                : (JSON.stringify(objOrMsg) ?? '');
        }

        runtime.runSync(
          Effect.logWithLevel(methodLevel)(message, cause).pipe(
            Effect.annotateLogs(annotations),
            Effect.provideService(References.MinimumLogLevel, level),
          ),
        );
      };

    return {
      debug: log('Debug'),
      info: log('Info'),
      warn: log('Warn'),
      error: log('Error'),
      child: (childBindings, childOptions) =>
        make({ ...bindings, ...childBindings }, childOptions?.level ?? level),
    };
  };

  return make(options.base ?? {}, options.level ?? 'Info');
}
